import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  IconButton,
  FormHelperText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useRecoilState } from 'recoil';
import { removeKeysFromObj, isValidNumber } from '../../../../utils/helper.jsx';
import {
  templateFormAtom,
  templateFormButtonsAtom,
  templateFormErrorsAtom,
} from '../../state/templateAtoms';
import TemplateSearchableSelect from '../shared/TemplateSearchableSelect';
import Field from '../ui/Field';
import FormSection from '../ui/FormSection';
import { typography , radius, radiusPx , spacing ,colors} from '../../../../theme/tokens';

const BUTTON_TYPE_GROUPS = [
  {
    label: 'Quick reply buttons',
    options: [{ label: 'Custom (Maximum 10 buttons)', type: 'QUICK_REPLY', isDisabled: false }],
  },
  {
    label: 'Call-to-action buttons',
    options: [
      { label: 'Visit Website (Maximum 2 buttons)', type: 'URL', isDisabled: false },
      { label: 'Call Phone Number (Maximum 1 buttons)', type: 'PHONE_NUMBER', isDisabled: false },
      { label: 'Offer Code (Maximum 1 buttons)', type: 'OFFER_CODE', isDisabled: false },
    ],
  },
];

const URL_TYPE_OPTIONS = [
  { label: 'Static', value: 'static' },
  { label: 'Dynamic', value: 'dynamic' },
];

const callToActionGridTypography = {
  '& .MuiInputBase-input, & .MuiSelect-select, & .MuiFormLabel-root, & .MuiTypography-root, & .MuiFormHelperText-root': {
    fontSize: typography.fontSizeSm,
    fontWeight: typography.fontWeightMedium,
  },
};

function applyButtonLimits(groups, buttonArrayObj) {
  const all = [...(buttonArrayObj.callAction || []), ...(buttonArrayObj.quickReply || [])];
  return groups.map((group, groupIndex) => ({
    ...group,
    options: group.options.map((opt) => {
      if (opt.type === 'QUICK_REPLY') {
        return { ...opt, isDisabled: all.filter((b) => b.type === 'QUICK_REPLY').length > 9 };
      }
      if (opt.type === 'URL') {
        return { ...opt, isDisabled: all.filter((b) => b.type === 'URL').length >= 2 };
      }
      if (opt.type === 'PHONE_NUMBER') {
        return { ...opt, isDisabled: all.filter((b) => b.type === 'PHONE_NUMBER').length >= 1 };
      }
      if (opt.type === 'OFFER_CODE') {
        return { ...opt, isDisabled: all.filter((b) => b.type === 'OFFER_CODE').length >= 1 };
      }
      return opt;
    }),
  }));
}

export default function TemplateButtonInputs() {
  const [formData, setFormData] = useRecoilState(templateFormAtom);
  const [buttonArrayObj, setButtonArrayObj] = useRecoilState(templateFormButtonsAtom);
  const [formErrors] = useRecoilState(templateFormErrorsAtom);
  const [selectedButton, setSelectedButton] = useState(null);

  const buttonTypeList = useMemo(
    () => applyButtonLimits(BUTTON_TYPE_GROUPS, buttonArrayObj),
    [buttonArrayObj]
  );

  const flatOptions = useMemo(
    () => buttonTypeList.flatMap((group) => group.options),
    [buttonTypeList]
  );

  const syncButtonsToForm = async (updateBtnObj) => {
    const newBtnArr = await removeKeysFromObj(
      [...(updateBtnObj.callAction || []), ...(updateBtnObj.quickReply || [])],
      ['label', 'isDisabled']
    );
    const isButtonObj = formData?.components?.find((c) => c.type === 'BUTTONS');
    if (isButtonObj) {
      setFormData({
        ...formData,
        components: formData.components.map((c) =>
          c.type === 'BUTTONS' ? { ...c, buttons: newBtnArr } : c
        ),
      });
    } else if (newBtnArr.length) {
      setFormData({
        ...formData,
        components: [...(formData.components || []), { type: 'BUTTONS', buttons: newBtnArr }],
      });
    }
  };

  const handleSelectButton = (option) => {
    if (!option) return;
    let updateBtnObj = { ...buttonArrayObj };
    if (option.type === 'QUICK_REPLY') {
      updateBtnObj = {
        ...updateBtnObj,
        quickReply: [...(updateBtnObj.quickReply || []), { ...option, text: '' }],
      };
    } else {
      updateBtnObj = {
        ...updateBtnObj,
        callAction: [...(updateBtnObj.callAction || []), { ...option, text: '' }],
      };
    }
    setButtonArrayObj(updateBtnObj);
    syncButtonsToForm(updateBtnObj);
    setSelectedButton(null);
  };

  const removeButtonObj = async (buttonObj, index, buttonType) => {
    let updateBtnObj = { ...buttonArrayObj };
    if (buttonObj?.type === 'QUICK_REPLY') {
      updateBtnObj = {
        ...updateBtnObj,
        quickReply: updateBtnObj.quickReply.filter((_, i) => i !== index),
      };
    } else {
      updateBtnObj = {
        ...updateBtnObj,
        callAction: updateBtnObj.callAction.filter((_, i) => i !== index),
      };
    }
    setButtonArrayObj(updateBtnObj);
    await syncButtonsToForm(updateBtnObj);
  };

  const handleChangeBtnText = async (value, type, index, field) => {
    const updateBtnObj = { ...buttonArrayObj };
    const arr = [...updateBtnObj[type]];
    let obj = { ...arr[index] };

    if (field === 'button') obj = { ...obj, text: value };
    else if (field === 'url') obj = { ...obj, url: value };
    else if (field === 'phone_number' && (isValidNumber(value) || !value) && value.length <= 20) {
      obj = { ...obj, phone_number: value };
    }

    arr[index] = obj;
    updateBtnObj[type] = arr;
    setButtonArrayObj(updateBtnObj);
    await syncButtonsToForm(updateBtnObj);
  };

  const handleSelectBtnType = (selected, type, index) => {
    const updateBtnObj = { ...buttonArrayObj };
    const arr = [...updateBtnObj[type]];
    let obj = { ...arr[index] };

    if (selected?.value === 'dynamic') {
      obj = { ...obj, example: [''], url: 'https://{{1}}' };
    } else {
      obj = { ...obj, url: 'https://' };
      delete obj.example;
    }

    arr[index] = obj;
    updateBtnObj[type] = arr;
    setButtonArrayObj(updateBtnObj);
  };

  const handleChangeUrlParams = async (value, type, index) => {
    const updateBtnObj = { ...buttonArrayObj };
    const arr = [...updateBtnObj[type]];
    arr[index] = { ...arr[index], example: [value || ''] };
    updateBtnObj[type] = arr;
    setButtonArrayObj(updateBtnObj);
    await syncButtonsToForm(updateBtnObj);
  };

  useEffect(() => {
    const buttonArr = formData?.components?.find((c) => c.type === 'BUTTONS')?.buttons;
    if (!buttonArr?.length) return;
    const updateObj = { callAction: [], quickReply: [] };
    buttonArr.forEach((button) => {
      if (button.type === 'QUICK_REPLY') updateObj.quickReply.push(button);
      else updateObj.callAction.push(button);
    });
    setButtonArrayObj(updateObj);
  }, [formData?.components]);

  const renderTextField = (buttonObj, buttonType, index) => (
    <Field
       sx={{
        '& .MuiOutlinedInput-root': {
          minHeight: '38px',
          borderRadius: radiusPx.xs,
        },
        '& .MuiFormLabel-root.MuiInputLabel-formControl.MuiInputLabel-root':{
          top:'0.7px',
          fontWeight:typography.fontWeightMedium,
          color:colors.grey[900]
        }
      }}
      label="Button Text"
      placeholder="Button Text"
      value={buttonObj?.text || ''}
      onChange={(e) => handleChangeBtnText(e.target.value, buttonType, index, 'button')}
      maxLength={25}
      error={Boolean(
        formErrors[`callActionBtn_text_${index}`] || formErrors[`quickReplyBtn_${index}`]
      )}
      errorMessage={
        formErrors[`callActionBtn_text_${index}`] || formErrors[`quickReplyBtn_${index}`]
      }
      helperText={`${buttonObj?.text?.length || 0}/25 characters`}
    />
  );

  return (
    <FormSection title="Buttons" description="Optional quick reply and call-to-action buttons">
      <Grid container spacing={2} mb={0}>
        <Grid item xs={12} sm={4}>
          <TemplateSearchableSelect
            label="Add Buttons"
            placeholder="Select button type"
            options={flatOptions}
            labelKey="label"
            valueKey="type"
            value={selectedButton}
            onChange={(val) => {
              setSelectedButton(val);
              if (val) handleSelectButton(val);
            }}
            getOptionDisabled={(option) => option?.isDisabled}
            getOptionLabel={(option) => option?.label || option?.type}
            isOptionEqualToValue={(option, value) => option?.type === value?.type}
          />
        </Grid>
      </Grid>

      {Object.entries(buttonArrayObj).map(([buttonType, array]) =>
        array?.length > 0 ? (
          <Box key={buttonType} mt={2}>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>
              {buttonType === 'callAction' ? 'Call to action' : 'Quick reply'}
            </Typography>

            {array.map((buttonObj, index) => {
              const removeBtn = (
                <IconButton onClick={() => removeButtonObj(buttonObj, index, buttonType)} size="small">
                  <CloseIcon fontSize="small" />
                </IconButton>
              );

              if (buttonObj.type === 'URL') {
                return (
                  <Grid
                    container
                    spacing={2}
                    key={index}
                    alignItems="flex-start"
                    mb={2}
                    sx={callToActionGridTypography}
                  >
                    <Grid item xs={12} sm={3}>
                      <TemplateSearchableSelect
                        label="Visit Website"
                        options={[{ label: 'Visit Website', value: 'URL' }]}
                        value={{ label: 'Visit Website', value: 'URL' }}
                        onChange={() => {}}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>{renderTextField(buttonObj, buttonType, index)}</Grid>
                    <Grid item xs={12} sm={2}>
                      <TemplateSearchableSelect
                        label="URL Type"
                        options={URL_TYPE_OPTIONS}
                        value={
                          buttonObj?.example?.length
                            ? URL_TYPE_OPTIONS[1]
                            : URL_TYPE_OPTIONS[0]
                        }
                        onChange={(val) => handleSelectBtnType(val, buttonType, index)}
                        sx={{
                          '& .MuiIconButton-root': {
                            marginRight: - spacing.size1,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Field
                        label="Website Url"
                        value={buttonObj?.url || ''}
                        onChange={(e) =>
                          handleChangeBtnText(e.target.value, buttonType, index, 'url')
                        }
                        maxLength={2000}
                        error={Boolean(formErrors[`callActionBtn_url_${index}`])}
                        errorMessage={formErrors[`callActionBtn_url_${index}`]}
                        helperText={`${buttonObj?.url?.length || 0}/2000 characters`}
                        sx={{ '& .MuiFormLabel-root.MuiInputLabel-formControl.MuiInputLabel-root':{
                          top:'0.7px',
                          fontWeight:typography.fontWeightMedium,
                          color:colors.grey[900]
                        }}} 
                      />
                      {buttonObj?.example?.length === 1 && (
                        <Field
                          placeholder="Enter Params"
                          value={buttonObj.example[0] || ''}
                          onChange={(e) =>
                            handleChangeUrlParams(e.target.value, buttonType, index)
                          }
                          sx={{ mt: 1 }}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} sm={1} sx={{
                        height: '-webkit-fill-available',
                        alignContent: 'center',
                      }}>
                      {removeBtn}
                    </Grid>
                  </Grid>
                );
              }

              if (buttonObj.type === 'PHONE_NUMBER') {
                return (
                  <Grid
                    container
                    spacing={2}
                    key={index}
                    alignItems="flex-start"
                    mb={2}
                    sx={callToActionGridTypography}
                  >
                    <Grid item xs={12} sm={3}>
                      <TemplateSearchableSelect
                        label="Call Phone Number"
                        options={[{ label: 'Call Phone Number', value: 'PHONE_NUMBER' }]}
                        value={{ label: 'Call Phone Number', value: 'PHONE_NUMBER' }}
                        onChange={() => {}}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>{renderTextField(buttonObj, buttonType, index)}</Grid>
                    <Grid item xs={12} sm={2}>
                      <TemplateSearchableSelect
                        label="Country"
                        options={[{ label: 'IN +91', value: 'IN' }]}
                        value={{ label: 'IN +91', value: 'IN' }}
                        onChange={() => {}}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Field
                        label="Phone Number"
                        placeholder="1236547890"
                        value={buttonObj?.phone_number || ''}
                        onChange={(e) =>
                          handleChangeBtnText(e.target.value, buttonType, index, 'phone_number')
                        }
                        maxLength={20}
                        error={Boolean(formErrors[`callActionBtn_phone_number_${index}`])}
                        errorMessage={formErrors[`callActionBtn_phone_number_${index}`]}
                        sx={{ '& .MuiFormLabel-root.MuiInputLabel-formControl.MuiInputLabel-root':{
                          top:'0.7px',
                          fontWeight:typography.fontWeightMedium,
                          color:colors.grey[900]
                        }}} 
                      />
                    </Grid>
                    <Grid item xs={12} sm={1} sx={{
                        height: '-webkit-fill-available',
                        alignContent: 'center',
                      }}>{removeBtn}</Grid>
                  </Grid>
                );
              }

              if (buttonObj.type === 'OFFER_CODE') {
                return (
                  <Grid
                    container
                    spacing={2}
                    key={index}
                    alignItems="flex-start"
                    mb={2}
                    sx={callToActionGridTypography}
                  >
                    <Grid item xs={12} sm={3}>
                      <TemplateSearchableSelect
                        label="Copy Offer Code"
                        options={[{ label: 'Copy Offer Code', value: 'OFFER_CODE' }]}
                        value={{ label: 'Copy Offer Code', value: 'OFFER_CODE' }}
                        onChange={() => {}}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>{renderTextField(buttonObj, buttonType, index)}</Grid>
                    <Grid item xs={12} sm={1} sx={{
                        height: '-webkit-fill-available',
                        alignContent: 'center',
                      }}>{removeBtn}</Grid>
                  </Grid>
                );
              }

              return (
                <Grid container spacing={2} key={index} alignItems="center" mb={2}>
                  <Grid item xs={11}>{renderTextField(buttonObj, buttonType, index)}</Grid>
                  <Grid item xs={1} sx={{
                      height: '-webkit-fill-available',
                      alignContent: 'center',
                    }}>
                    {removeBtn}
                  </Grid>
                </Grid>
              );
            })}
          </Box>
        ) : null
      )}

      <FormHelperText>Optional — up to 10 quick replies, 2 URL, 1 phone, 1 offer code</FormHelperText>
    </FormSection>
  );
}
